/* Loupe – cart extras: gift wrapping, order note, discount codes, delivery estimate.
   Markup: snippets/cart-extras.liquid + snippets/cart-delivery.liquid, rendered in the cart drawer and on the cart page.
   Both are replaced after every cart change (window.LoupeCart.render, Section Rendering API), so each new element
   wires itself up in connectedCallback; state that must outlive a re-render (open panels, an unsaved note) lives in `memory`.
   Configuration: <script type="application/json" id="cart-extras-config"> printed by sections/cart-extras.liquid (footer group). */
(() => {
  if (customElements.get('loupe-cart-extras')) return;   // the script tag is on the drawer and the cart page

  const L = () => window.Loupe || {};
  const config = () => {
    const el = document.getElementById('cart-extras-config');
    try { return el ? JSON.parse(el.textContent) : {}; } catch (e) { return {}; }
  };
  const url = (key, fallback) => ((L().routes || {})[key] || fallback) + '.js';
  const errorText = () => (L().strings || {}).error || 'Something went wrong.';
  const memory = { open: {}, note: null };   // survives re-renders

  async function request(path, body) {
    const opts = { headers: { Accept: 'application/json' } };
    if (body) Object.assign(opts, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json', Accept: 'application/json' } });
    const r = await fetch(path, opts);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.description || j.message || errorText());
    return j;
  }
  // ask Shopify to return the cart sections with the response, like theme.js does
  const withSections = (body) => {
    const c = window.LoupeCart;
    if (c) { body.sections = c.sections(); body.sections_url = location.pathname; }
    return body;
  };
  const render = (j) => { const c = window.LoupeCart; if (!c) return location.reload(); j && j.sections ? c.render(j.sections) : c.refresh(); };
  const appliedCodes = async () => ((await request(url('cart', '/cart'))).discount_codes || []).filter((d) => d.applicable).map((d) => d.code);
  const same = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

  /* ---------- order note: saved with a debounce, never re-renders (keeps focus while typing) ---------- */
  let noteTimer, noteChain = Promise.resolve();
  function saveNote() {
    clearTimeout(noteTimer);
    const value = memory.note;
    if (value === null) return noteChain;
    noteChain = noteChain.then(async () => {
      const boxes = [...document.querySelectorAll('loupe-cart-extras')];
      try {
        await request(url('cartUpdate', '/cart/update'), { note: value });
        if (memory.note === value) memory.note = null;
        boxes.forEach((b) => {
          const ta = b.querySelector('[data-cx-note-input]');
          if (ta && ta !== document.activeElement && ta.value !== value) ta.value = value;   // keep drawer + page in sync
          b.status(b.dataset.msgSaved);   // only visible (and announced) where the note panel is open
        });
      } catch (e) { boxes.forEach((b) => b.status(e.message || errorText())); }
    });
    return noteChain;
  }

  class LoupeCartExtras extends HTMLElement {
    connectedCallback() {
      if (this.ready) return; this.ready = true;
      this.ctx = this.dataset.context || 'page';
      ['click', 'change', 'input', 'keydown', 'focusout'].forEach((t) => this.addEventListener(t, this));
      this.apply();
    }

    q(s) { return this.querySelector(s); }

    lines() {
      const v = (this.dataset.variants || '').split(','), k = (this.dataset.keys || '').split(',');
      return v.filter(Boolean).map((id, i) => ({ variant: +id, key: k[i] }));
    }

    /* show/fill rows from the config; called on connect and when the config section reloads in the editor */
    apply() {
      const c = config();
      const note = this.q('[data-cx-note]');
      if (note) note.hidden = !(c.note || note.hasAttribute('data-cx-always'));
      const disc = this.q('[data-cx-discount]');
      if (disc) disc.hidden = !c.discount;

      const row = this.q('[data-cx-gift]'), g = c.gift;
      if (row) {
        row.hidden = !(g && g.variant);
        if (!row.hidden) {
          this.q('[data-cx-gift-label]').textContent = g.label || '';
          this.q('[data-cx-gift-price]').textContent = g.price || '';
          const t = this.q('[data-cx-gift-text]'); t.textContent = g.text || ''; t.hidden = !g.text;
          this.q('[data-cx-gift-input]').checked = this.lines().some((l) => l.variant === +g.variant);
        }
      }

      this.querySelectorAll('[data-cx-toggle]').forEach((b) => {
        const k = this.ctx + ':' + b.dataset.cxToggle;
        this.setOpen(b, k in memory.open ? memory.open[k] : b.getAttribute('aria-expanded') === 'true');
      });
      const ta = this.q('[data-cx-note-input]');
      if (ta && memory.note !== null && ta.value !== memory.note) {   // re-rendered before the note was saved: keep the draft
        ta.value = memory.note; this.setOpen(this.q('[data-cx-toggle=note]'), true);
      }
    }

    setOpen(btn, open) {
      if (!btn) return;
      btn.setAttribute('aria-expanded', open);
      const p = document.getElementById(btn.getAttribute('aria-controls')); if (p) p.hidden = !open;
    }

    handleEvent(e) {
      const t = e.target;
      if (e.type === 'click') {
        const tog = t.closest('[data-cx-toggle]');
        if (tog) {
          const open = tog.getAttribute('aria-expanded') !== 'true';
          this.setOpen(tog, open); memory.open[this.ctx + ':' + tog.dataset.cxToggle] = open;
          if (open && tog.dataset.cxToggle === 'discount') this.q('[data-cx-code]').focus();
          return;
        }
        if (t.closest('[data-cx-apply]')) return this.applyCode();
        const chip = t.closest('[data-cx-remove-code]'); if (chip) return this.removeCode(chip.dataset.cxRemoveCode);
      }
      if (e.type === 'change' && t.matches('[data-cx-gift-input]')) return this.gift(t);
      if (e.type === 'input' && t.matches('[data-cx-note-input]')) {
        memory.note = t.value; this.status('');
        clearTimeout(noteTimer); noteTimer = setTimeout(saveNote, 700);
      }
      if (e.type === 'input' && t.matches('[data-cx-code]') && t.getAttribute('aria-invalid') === 'true') this.error('');
      if (e.type === 'keydown' && e.key === 'Enter' && t.matches('[data-cx-code]')) { e.preventDefault(); this.applyCode(); }
      if (e.type === 'focusout' && t.matches('[data-cx-note-input]')) saveNote();
    }

    busy(on) { this.classList.toggle('is-busy', on); this.setAttribute('aria-busy', on); }
    status(msg) {
      const s = this.q('[data-cx-note-status]'); if (!s) return;
      s.textContent = msg || ''; clearTimeout(this.st); if (msg) this.st = setTimeout(() => (s.textContent = ''), 2500);
    }
    error(msg, focus) {
      const out = this.q('[data-cx-code-error]'), inp = this.q('[data-cx-code]');
      if (out) out.textContent = msg || '';
      if (inp) { msg ? inp.setAttribute('aria-invalid', 'true') : inp.removeAttribute('aria-invalid'); if (msg && focus) inp.focus(); }
    }
    announce(msg) { const live = this.q('[data-cx-live]'); if (live && msg) setTimeout(() => (live.textContent = msg), 150); }

    /* after a cart change: re-render drawer + page, then restore focus and announce in the fresh element */
    done(j, focusId, msg) {
      render(j);
      const fresh = document.querySelector(`loupe-cart-extras[data-context="${this.ctx}"]`);
      if (!fresh) return;
      const f = focusId && document.getElementById(focusId);
      if (f && !f.closest('[hidden]')) f.focus({ preventScroll: true });
      fresh.announce(msg);
    }

    async applyCode() {
      const inp = this.q('[data-cx-code]'), code = inp ? inp.value.trim() : '';
      if (!code) return this.error(this.dataset.msgEmpty, true);
      this.error(''); this.busy(true);
      try {
        await saveNote();
        const existing = await appliedCodes();
        if (existing.some((c) => same(c, code))) { inp.value = ''; return this.error(this.dataset.msgExists, true); }
        const j = await request(url('cartUpdate', '/cart/update'), withSections({ discount: [...existing, code].join(',') }));
        const list = j.discount_codes;
        const hit = Array.isArray(list) && list.find((d) => same(d.code, code));
        if (Array.isArray(list) && !(hit && hit.applicable)) {
          // not valid for this cart: take it back off so checkout stays clean, keep the panel as it is
          await request(url('cartUpdate', '/cart/update'), { discount: existing.join(',') }).catch(() => {});
          return this.error(this.dataset.msgInvalid, true);
        }
        memory.open[this.ctx + ':discount'] = false;
        this.done(j, `cx-${this.ctx}-discount-toggle`, this.dataset.msgApplied);
      } catch (x) { this.error(x.message || errorText(), true); }
      finally { this.busy(false); }
    }

    async removeCode(code) {
      this.error(''); this.busy(true);
      try {
        await saveNote();
        const rest = (await appliedCodes()).filter((c) => !same(c, code));
        const j = await request(url('cartUpdate', '/cart/update'), withSections({ discount: rest.join(',') }));
        this.done(j, `cx-${this.ctx}-discount-toggle`, this.dataset.msgRemoved);
      } catch (x) { this.error(x.message || errorText()); }
      finally { this.busy(false); }
    }

    async gift(cb) {
      const g = config().gift; if (!g) return;
      const want = cb.checked, err = this.q('[data-cx-gift-error]');
      if (err) err.textContent = '';
      this.busy(true);
      try {
        await saveNote();
        let j;
        if (want) {
          j = await request(url('cartAdd', '/cart/add'), withSections({ items: [{ id: +g.variant, quantity: 1 }] }));
        } else {
          const updates = {};
          this.lines().filter((l) => l.variant === +g.variant).forEach((l) => (updates[l.key] = 0));
          j = await request(url('cartUpdate', '/cart/update'), withSections({ updates }));
        }
        this.done(j, cb.id, want ? this.dataset.msgGiftAdded : this.dataset.msgGiftRemoved);
      } catch (x) {
        cb.checked = !want;
        if (err) err.textContent = x.message || errorText();
      } finally { this.busy(false); }
    }
  }
  customElements.define('loupe-cart-extras', LoupeCartExtras);

  /* ---------- delivery estimate: today + min/max business days (Saturdays and Sundays skipped) ---------- */
  const addBusinessDays = (n) => {
    const d = new Date(); d.setHours(12, 0, 0, 0);
    for (let added = 0; added < n;) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) added++; }
    return d;
  };
  class LoupeCartDelivery extends HTMLElement {
    connectedCallback() { this.apply(); }
    apply() {
      const c = config(), d = c.delivery, out = this.querySelector('[data-cx-delivery-text]');
      if (!d || !out || !d.text) { this.hidden = true; return; }
      let a = Math.max(0, +d.min || 0), b = Math.max(0, +d.max || 0);
      if (a > b) [a, b] = [b, a];
      const from = addBusinessDays(a), to = addBusinessDays(b);
      let fmt;
      try { fmt = new Intl.DateTimeFormat(c.locale || document.documentElement.lang || undefined, { weekday: 'short', day: 'numeric', month: 'short' }); }
      catch (e) { fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' }); }
      let text = d.text;
      if (a === b) text = text.replace(/\[from\][\s–—-]*\[to\]/, '[from]');   // one day, not a range
      out.textContent = text.replace(/\[from\]/g, fmt.format(from)).replace(/\[to\]/g, fmt.format(to));
      this.hidden = false;
    }
  }
  customElements.define('loupe-cart-delivery', LoupeCartDelivery);

  /* theme editor: the config section was edited / added / removed */
  const reapply = () => document.querySelectorAll('loupe-cart-extras, loupe-cart-delivery').forEach((el) => el.apply());
  document.addEventListener('shopify:section:load', (e) => { if (e.target.querySelector && e.target.querySelector('#cart-extras-config')) reapply(); });
  document.addEventListener('shopify:section:unload', () => setTimeout(reapply));
})();
